import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.css';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

// Define a menu structure with categories and children
interface MenuItem {
  label: string;
  path?: string; // if this item is a direct link
  children?: MenuItem[]; // if this item has sub-items
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  // Track which categories are expanded. Keys are category labels.
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const menuItems: MenuItem[] = [
    {
      label: t('SideMenu.Labels.Orders'),
      children: [
        { label: t('CreateOrder.Title'), path: '/create-order' },
        { label: t('OrdersList.Title'), path: '/orders' },
        // { label: 'Order Management', path: '/order-management' },
      ],
    },
    {
      label: t('SideMenu.Labels.Users'),
      children: [
        { label: t('CreateUser.Title'), path: '/create-user' },
        { label: t('UsersList.Title'), path: '/users' },
      ],
    },
    {
      label: t('SideMenu.Labels.Deliveries'),
      children: [
        { label: t('SiloDeliveries.Title'), path: '/silo-deliveries' },
        { label: t('DeliveryCertificates.Title'), path: '/delivery-certificates' },
      ],
    },
    {
      label: t('SideMenu.Labels.Reports'),
      children: [
        { label: t('ClientReportGeneration.Title'), path: '/client-report-generation' },
      ],
    },
    {
      label: t('ProductIdCreation.Title'),
      path: '/product-id-creation',
    },
    // Add more categories or direct links as needed
  ];

  const toggleCategory = (label: string) => {
    setExpanded(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
      <nav>
        <ul className={styles.navList}>
          {menuItems.map((item) => (
            <li key={item.label} className={styles.category}>
              {item.children ? (
                <>
                  <div
                    className={styles.categoryHeader}
                    onClick={() => toggleCategory(item.label)}
                  >
                    {item.label}
                    <span className={styles.arrow}>
                      {expanded[item.label] ? '▲' : '▼'}
                    </span>
                  </div>
                  <ul className={`${styles.subMenu} ${expanded[item.label] ? styles.openSubMenu : ''}`}>
                    {item.children.map(child => (
                      <li key={child.label}>
                        <NavLink
                          to={child.path || '#'}
                          className={({ isActive }) =>
                            isActive ? styles.activeLink : ''
                          }
                          onClick={onClose}
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <NavLink
                  to={item.path || '#'}
                  className={({ isActive }) =>
                    isActive ? styles.activeLink : ''
                  }
                  onClick={onClose}
                >
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;